import { FxFolderCard, FxIcon } from '@/components/fx'

import { FolderActionsMenu } from './FolderActionsMenu'
import { formatDate, templateCountLabel, type ProjectNode } from './projectData'

/**
 * The view toggle's second half: the design system's folder cards.
 *
 * Opening a card switches back to the list with that project expanded,
 * because there is no per-project screen to navigate to — `/projekte/:id`
 * does not exist in this wave, and a card that led to a 404 would be worse
 * than one that shows what is inside it where it can be shown.
 *
 * The kebab sits in the caption line rather than in `FxFolderCard`'s own
 * slot: the card's built-in menu button is a `<button>`, and the Radix menu
 * trigger cannot be nested inside one.
 */
export function ProjectGrid({
  projects,
  favorites,
  onOpen,
  onToggleFavorite,
  onRename,
  onShare,
  onDelete,
}: {
  projects: ProjectNode[]
  favorites: ReadonlySet<string>
  onOpen: (project: ProjectNode) => void
  onToggleFavorite: (folderId: string) => void
  onRename: (project: ProjectNode) => void
  onShare: (project: ProjectNode) => void
  onDelete: (project: ProjectNode) => void
}) {
  return (
    <ul className="m-0 flex list-none flex-wrap gap-8 p-0">
      {projects.map((project) => {
        const id = project.folder.id
        const isFavorite = favorites.has(id)
        return (
          <li key={id} className="flex w-card-folder max-w-full flex-col gap-2">
            <FxFolderCard name={project.folder.name} onOpen={() => onOpen(project)} />
            <div className="flex items-center gap-1">
              <span className="min-w-0 flex-1 text-small text-text-muted">
                {templateCountLabel(project.templateCount)}
                {project.updatedAt != null && ` · ${formatDate(project.updatedAt)}`}
              </span>
              <button
                type="button"
                className="flex h-9 w-9 flex-none items-center justify-center rounded-sm text-primary-text"
                aria-pressed={isFavorite}
                aria-label={`„${project.folder.name}“ als Favorit merken`}
                onClick={() => onToggleFavorite(id)}
              >
                <FxIcon name="bookmark" size={22} filled={isFavorite} />
              </button>
              <FolderActionsMenu
                name={project.folder.name}
                onRename={() => onRename(project)}
                onShare={() => onShare(project)}
                onDelete={() => onDelete(project)}
              />
            </div>
          </li>
        )
      })}
    </ul>
  )
}
